import { useState } from "react"
import { useNavigate } from "react-router-dom"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import UserFormModal from "../../components/admin/UserFormModal"
import UserViewModal from "../../components/admin/UserViewModal"
import UsersTable from "../../components/admin/UsersTable"
import { useGet } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { api } from "../../utils/apiComponent/apiClient"
import { toastUtils } from "../../utils/toast"
import { 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Plus, 
  CheckCircle, 
  XCircle, 
  PauseCircle, 
  Ban,
  X
} from "lucide-react"

export default function AdminManagementPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [viewingUser, setViewingUser] = useState(null)

  // Récupération des vraies données depuis la base
  const { data: usersData, loading, error, execute: refetchAdmins } = useGet(endpoints.users)
  
  // Extraire les utilisateurs et filtrer seulement les admins
  const users = usersData ? (Array.isArray(usersData) ? usersData : usersData.users || []) : []
  
  console.log('[AdminManagementPage] usersData:', usersData)
  console.log('[AdminManagementPage] users:', users)
  console.log('[AdminManagementPage] users.length:', users.length)
  console.log('[AdminManagementPage] Exemple user:', users[0])
  
  const admins = users.filter(user => {
    const role = (user.role || '').toString().toLowerCase()
    const isAdmin = role === 'admin' || role === 'administrateur' || role === 'super admin' || role === 'superadmin' || role === 'super_admin'
    if (!isAdmin) {
      console.log('[AdminManagementPage] User filtré (pas admin):', { name: user.name, role: user.role })
    }
    return isAdmin
  })
  
  console.log('[AdminManagementPage] admins:', admins)
  console.log('[AdminManagementPage] admins.length:', admins.length)

  const filteredAdmins = admins.filter(admin =>
    admin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(admin =>
    filterStatus === "all" ? true : admin.status === filterStatus
  )

  const getStatusColor = (status) => {
    switch (status) {
      case "Actif": return "bg-blue-50 text-[#203a8a]";
      case "Suspendu": return "bg-gray-100 text-gray-800";
      case "Inactif": return "bg-gray-100 text-gray-800";
      case "Banni": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  }

  const handleCreateAdmin = () => {
    setEditingUser(null)
    setIsUserModalOpen(true)
  }

  const handleEditAdmin = (admin) => {
    setEditingUser(admin)
    setIsUserModalOpen(true)
  }

  const handleViewAdmin = (admin) => {
    setViewingUser(admin)
  }

  const handleUserSuccess = async () => {
    setEditingUser(null)
    setIsUserModalOpen(false)
    await new Promise(resolve => setTimeout(resolve, 500))
    refetchAdmins()
  }

  const handleDeleteAdmin = async (admin) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${admin.name} ?`)) {
      try {
        console.log('[AdminManagementPage] Suppression de l\'admin:', admin.id, admin.email)
        const response = await api.delete(endpoints.deleteUser(admin.id))
        console.log('[AdminManagementPage] Réponse de suppression:', response)
        console.log('[AdminManagementPage] Admin supprimé:', admin.email)
        
        toastUtils.success("Admin supprimé avec succès !")
        
        // Attendre un peu avant de rafraîchir pour que le backend enregistre la suppression
        await new Promise(resolve => setTimeout(resolve, 500))
        await refetchAdmins()
        console.log('[AdminManagementPage] Liste rafraîchie après suppression')
      } catch (error) {
        console.error('[AdminManagementPage] Erreur suppression:', error)
        console.error('[AdminManagementPage] Détails erreur:', {
          message: error.message,
          status: error.status,
          details: error.details
        })
        toastUtils.error(error.message || "Erreur lors de la suppression de l'admin")
      }
    }
  }

  // Afficher un loader pendant le chargement
  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminSidebar />
        <main className="flex-1 p-8 ml-64 max-w-full overflow-x-hidden flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#203a8a] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des admins...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
      <AdminSidebar />

      {/* Main Content */}
      <main className="flex-1 p-8 ml-64 max-w-full overflow-x-hidden">
        <AdminHeader
          title="Gestion des admins"
          subtitle="Gérer les administrateurs de la plateforme"
          showCreateButton={true}
          createButtonText="Créer un admin"
          onCreateClick={handleCreateAdmin}
        />

        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-grow">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="Actif">Actif</option>
              <option value="Inactif">Inactif</option>
              <option value="Suspendu">Suspendu</option>
              <option value="Banni">Banni</option>
            </select>
          </div>
        </div>

        {/* Admins Table */}
        <UsersTable 
          users={filteredAdmins}
          onView={handleViewAdmin}
          onEdit={handleEditAdmin}
          onDelete={handleDeleteAdmin}
        />
      </main>

      {/* User Form Modal */}
      <UserFormModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        user={editingUser}
        onSuccess={handleUserSuccess}
        defaultRole="admin"
      />

      {/* User View Modal */}
      <UserViewModal
        isOpen={!!viewingUser}
        onClose={() => setViewingUser(null)}
        user={viewingUser}
      />

      {/* Create Admin Modal (design original) */}
      {false && (
        <div className="fixed inset-0 bg-transparent flex items-center justify-center z-50 p-4" style={{backdropFilter: 'blur(8px)'}}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Créer un nouvel admin</h2>
              <button
                onClick={handleCloseCreateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-6">
              {/* Informations personnelles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={createFormData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                    placeholder="Prénom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={createFormData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                    placeholder="Nom"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={createFormData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  placeholder="admin@example.com"
                />
              </div>

              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rôle *
                </label>
                <select
                  name="role"
                  value={createFormData.role}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>

              {/* Informations optionnelles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={createFormData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={createFormData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                    placeholder="Paris"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pays
                </label>
                <input
                  type="text"
                  name="country"
                  value={createFormData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  placeholder="France"
                />
              </div>

              {/* Génération de mot de passe */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Mot de passe</h3>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-4 py-2 bg-[#203a8a] text-white rounded-lg hover:bg-[#1a2f6e] transition-colors"
                  >
                    Générer un mot de passe
                  </button>
                  {generatedPassword && (
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mot de passe généré
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={generatedPassword}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-mono text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(generatedPassword)}
                          className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
                        >
                          Copier
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  ⚠️ Copiez ce mot de passe avant de valider, il ne sera pas envoyé par email.
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleCloseCreateModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#203a8a] text-white rounded-lg hover:bg-[#1a2f6e] transition-colors"
                >
                  Créer l'admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
